import React, { useState, useRef } from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import client from 'shared/ApiClient';
import { RErrorText } from './RErrorText';
import { validateEmail } from 'shared';

interface REmailInputProps {
    value: string;
    onChange: (value: string, isValid: boolean) => void;
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    validateOnBlur?: boolean;
}

export function REmailInput({
    value,
    onChange,
    placeholder = 'Enter your email',
    disabled,
    fullWidth,
    validateOnBlur = false,
}: REmailInputProps) {
    const [touched, setTouched] = useState(false);
    const [serverError, setServerError] = useState('');
    const [validating, setValidating] = useState(false);
    const lastValidated = useRef('');

    const patternValid = validateEmail(value);
    const showPatternError = touched && value.length > 0 && !patternValid;
    const showError = showPatternError || !!serverError;
    const errorMessage = serverError || (showPatternError ? 'Please enter a valid email address.' : '');

    async function handleBlur() {
        setTouched(true);
        if (!validateOnBlur || !patternValid || lastValidated.current === value) return;

        lastValidated.current = value;
        setValidating(true);
        setServerError('');
        try {
            const res = await client('sendgrid/validate-email-input/', { body: { email: value } });
            if (!res.isValid) {
                setServerError('This email address appears to be invalid.');
                onChange(value, false);
            }
        } catch {
            setServerError('');
        } finally {
            setValidating(false);
        }
    }

    return (
        <Wrapper fullWidth={fullWidth}>
            <StyledInput
                type="email"
                value={value}
                onChange={(e) => {
                    setServerError('');
                    onChange(e.target.value, validateEmail(e.target.value));
                }}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled || validating}
                fullWidth={fullWidth}
                hasError={showError}
            />
            {validating && <HintText>Validating...</HintText>}
            {!validating && errorMessage && <RErrorText>{errorMessage}</RErrorText>}
        </Wrapper>
    );
}

const Wrapper = styled('div')<{ fullWidth?: boolean }>(({ fullWidth }) => ({
    width: fullWidth ? '100%' : 'auto',
}));

const StyledInput = styled('input')<{ fullWidth?: boolean; hasError?: boolean }>(({ fullWidth, hasError }) => ({
    width: fullWidth ? '100%' : 'auto',
    padding: '10px 14px',
    fontSize: 14,
    color: Colors.darkNavy,
    backgroundColor: Colors.faintGrey,
    border: `1px solid ${hasError ? Colors.mediumRed : Colors.lightGrey}`,
    borderRadius: 10,
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': {
        borderColor: hasError ? Colors.mediumRed : Colors.mediumGrey,
    },
    '&:disabled': {
        backgroundColor: Colors.lightGrey,
        color: Colors.mediumGrey,
    },
    '&::placeholder': {
        color: Colors.lightGrey,
    },
}));

const HintText = styled('span')({
    fontSize: 12,
    color: Colors.mediumGrey,
    marginTop: 4,
    display: 'block',
});
